import React, { useState } from 'react';
import Sidebar from '../components/Sidebar';
import apiClient, { API_BASE_URL } from '../utils/apiConfig';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';

// ═══════════════════════════════════════════════════════════════════════════════
// FUNCIONES AUXILIARES
// ═══════════════════════════════════════════════════════════════════════════════
const limpiarCaracteresEspeciales = (texto) => {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9\s\-_().@]/g, '');
};

const limpiarCedula = (texto) => {
  return texto.replace(/[^0-9]/g, '');
};

// Colores para identificar firmantes visualmente
const COLORES_FIRMANTES = [
  { bg: '#e3f2fd', border: '#1976d2', text: '#1565c0', nombre: 'Azul' },
  { bg: '#e8f5e9', border: '#388e3c', text: '#2e7d32', nombre: 'Verde' },
  { bg: '#fff3e0', border: '#f57c00', text: '#e65100', nombre: 'Naranja' },
  { bg: '#f3e5f5', border: '#7b1fa2', text: '#6a1b9a', nombre: 'Morado' },
  { bg: '#fce4ec', border: '#c62828', text: '#b71c1c', nombre: 'Rojo' },
  { bg: '#e0f7fa', border: '#00838f', text: '#006064', nombre: 'Cyan' },
  { bg: '#fff8e1', border: '#f9a825', text: '#f57f17', nombre: 'Amarillo' },
  { bg: '#efebe9', border: '#5d4037', text: '#4e342e', nombre: 'Café' },
];

const obtenerColor = (index) => {
  return COLORES_FIRMANTES[index % COLORES_FIRMANTES.length];
};

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════
function AdminSendDocumentPage() {
  const navigate = useNavigate();
  const [sidebarAbierto, setSidebarAbierto] = useState(true);
  const [paso, setPaso] = useState(1);
  const [mensaje, setMensaje] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [errors, setErrors] = useState({});

  // ═══════════════════════════════════════════════════════════════════════════
  // PASO 1: Documento
  // ═══════════════════════════════════════════════════════════════════════════
  const [documento, setDocumento] = useState(null);
  const [nombreDocumento, setNombreDocumento] = useState('');
  const [descripcionDocumento, setDescripcionDocumento] = useState('');

  // ═══════════════════════════════════════════════════════════════════════════
  // PASO 2: Firmantes
  // ═══════════════════════════════════════════════════════════════════════════
  const [firmantes, setFirmantes] = useState([
    { 
      id: 1, 
      nombre: '', 
      cedula: '', 
      correo: '', 
      celular: '', 
      tipoFirmante: 'externo',
      metodoEnvio: [],
      buscando: false
    }
  ]);
  const [contadorId, setContadorId] = useState(2);

  // ═══════════════════════════════════════════════════════════════════════════
  // PASO 3: Orden de Firma
  // ═══════════════════════════════════════════════════════════════════════════
  const [ordenFirma, setOrdenFirma] = useState('paralelo');

  // ═══════════════════════════════════════════════════════════════════════════
  // ⭐ ESTADOS PARA MEJORAS: Drag & Drop y Errores Visuales
  // ═══════════════════════════════════════════════════════════════════════════
  const [firmanteArrastrado, setFirmanteArrastrado] = useState(null);
  const [indiceDestino, setIndiceDestino] = useState(null);
  const [erroresVisuales, setErroresVisuales] = useState({});

  // ═══════════════════════════════════════════════════════════════════════════
  // FUNCIONES AUXILIARES
  // ═══════════════════════════════════════════════════════════════════════════
  const mostrarMensaje = (texto, tipo = 'info') => {
    const iconos = { success: '✅', error: '❌', warning: '⚠️' };
    setMensaje(`${iconos[tipo] || ''} ${texto}`);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // GESTIÓN DE FIRMANTES
  // ═══════════════════════════════════════════════════════════════════════════
  const agregarFirmante = () => {
    setFirmantes(prev => [...prev, {
      id: contadorId,
      nombre: '',
      cedula: '',
      correo: '',
      celular: '',
      tipoFirmante: 'externo',
      metodoEnvio: [],
      buscando: false
    }]);
    setContadorId(prev => prev + 1);
  };

  const eliminarFirmante = (id) => {
    if (firmantes.length <= 1) {
      mostrarMensaje('Debe haber al menos un firmante.', 'warning');
      return;
    }
    setFirmantes(prev => prev.filter(f => f.id !== id));
  };

  const actualizarFirmante = (id, campo, valor) => {
    setFirmantes(prev => prev.map(f => 
      f.id === id ? { ...f, [campo]: valor } : f
    ));
  };

  const toggleMetodoEnvio = (id, metodo) => {
    setFirmantes(prev => prev.map(f => {
      if (f.id !== id) return f;
      const metodos = f.metodoEnvio.includes(metodo)
        ? f.metodoEnvio.filter(m => m !== metodo)
        : [...f.metodoEnvio, metodo];
      return { ...f, metodoEnvio: metodos };
    }));
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // ⭐ BÚSQUEDA MEJORADA DE PERSONAS
  // ═══════════════════════════════════════════════════════════════════════════
  const buscarPersona = async (id, cedula) => {
    if (!cedula || cedula.length < 5) {
      mostrarMensaje('Ingresa al menos 5 dígitos de la cédula', 'warning');
      return;
    }
    
    actualizarFirmante(id, 'buscando', true);
    
    try {
      // PASO 1: Buscar en MongoDB (usuarios + documentos previos)
      try {
        const resMongo = await apiClient.get(`/api/buscar-persona/${cedula}`);
        
        if (resMongo.data.success) {
          const { data, tipo, fuente } = resMongo.data;
          
          setFirmantes(prev => prev.map(f => {
            if (f.id !== id) return f;
            return {
              ...f,
              nombre: data.nombres || '',
              correo: data.correo || '',
              celular: data.celular || '',
              tipoFirmante: tipo,
              buscando: false
            };
          }));
          
          const fuenteTexto = {
            'usuarios': 'Funcionario interno encontrado',
            'documentos': 'Encontrado en documentos previos',
            'firmantes': 'Encontrado como firmante anterior'
          };
          
          mostrarMensaje(`${fuenteTexto[fuente] || 'Persona encontrada'}: ${data.nombres}`, 'success');
          return;
        }
      } catch (errorMongo) {
        if (errorMongo.response?.status !== 404) {
          console.warn('⚠️ Error buscando en MongoDB:', errorMongo.message);
        }
      }

      // PASO 2: Buscar en SQL Server (pacientes)
      try {
        const resPaciente = await apiClient.get(`/api/paciente/${cedula}`, { timeout: 10000 });
        const { nombres, correo, celular } = resPaciente.data;
        
        setFirmantes(prev => prev.map(f => {
          if (f.id !== id) return f;
          return {
            ...f,
            nombre: nombres || '',
            correo: correo || '',
            celular: celular || '',
            tipoFirmante: 'externo',
            buscando: false
          };
        }));
        
        mostrarMensaje(`Persona encontrada: ${nombres}`, 'success');
        return;
      } catch (errorSQL) {
        if (errorSQL.response?.status === 404) {
          actualizarFirmante(id, 'buscando', false);
          mostrarMensaje('Persona no encontrada. Ingresa los datos manualmente.', 'warning');
          return;
        }
        throw errorSQL;
      }

    } catch (error) {
      console.error('❌ Error en búsqueda:', error);
      actualizarFirmante(id, 'buscando', false);
      mostrarMensaje('Error de conexión al buscar.', 'error');
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // ⭐ FUNCIONES DRAG & DROP
  // ═══════════════════════════════════════════════════════════════════════════
  const handleDragStart = (e, index) => {
    setFirmanteArrastrado(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
    setTimeout(() => {
      e.target.classList.add('firmante-dragging');
    }, 0);
  };

  const handleDragEnd = (e) => {
    e.target.classList.remove('firmante-dragging');
    setFirmanteArrastrado(null);
    setIndiceDestino(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIndiceDestino(index);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIndiceDestino(null);
    }
  };

  const handleDrop = (e, indexDestino) => {
    e.preventDefault();
    
    if (firmanteArrastrado === null || firmanteArrastrado === indexDestino) {
      setFirmanteArrastrado(null);
      setIndiceDestino(null);
      return;
    }
    
    const nuevaLista = [...firmantes];
    const [firmanteMovido] = nuevaLista.splice(firmanteArrastrado, 1);
    nuevaLista.splice(indexDestino, 0, firmanteMovido);
    
    setFirmantes(nuevaLista);
    setFirmanteArrastrado(null);
    setIndiceDestino(null);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // MANEJO DE ARCHIVO
  // ═══════════════════════════════════════════════════════════════════════════
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file?.type === 'application/pdf') {
      setDocumento(file);
      setErrors(prev => ({ ...prev, documento: '' }));
    } else if (file) {
      setErrors(prev => ({ ...prev, documento: 'Solo se permiten archivos PDF.' }));
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // ⭐ VALIDACIÓN MEJORADA CON ANIMACIONES
  // ═══════════════════════════════════════════════════════════════════════════
  const validarPaso = (pasoActual) => {
    const newErrors = {};
    const nuevosErroresVisuales = {};

    if (pasoActual === 1) {
      if (!documento) newErrors.documento = 'Debes subir un archivo PDF.';
      if (!nombreDocumento.trim()) newErrors.nombreDocumento = 'El nombre del documento es obligatorio.';
    }

    if (pasoActual === 2) {
      const erroresFirmantes = [];
      let primerErrorIndex = -1;
      
      firmantes.forEach((f, i) => {
        const ef = {};
        if (!f.nombre.trim()) ef.nombre = 'Nombre obligatorio';
        if (!f.cedula.trim()) ef.cedula = 'Cédula obligatoria';
        if (!f.correo.trim() && !f.celular.trim()) ef.contacto = 'Debe tener correo o celular';
        if (f.correo && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.correo)) ef.correo = 'Correo no válido';
        if (f.celular && !/^\d{10}$/.test(f.celular)) ef.celular = 'Celular debe tener 10 dígitos';
        
        // Validación mejorada de método de envío
        if (f.metodoEnvio.length === 0) {
          ef.metodoEnvio = 'Selecciona al menos un método de envío';
          nuevosErroresVisuales[`metodo-${f.id}`] = true;
          if (primerErrorIndex === -1) primerErrorIndex = i;
        }
        
        if (Object.keys(ef).length > 0) {
          erroresFirmantes[i] = ef;
          if (primerErrorIndex === -1) primerErrorIndex = i;
        }
      });

      // Verificar cédulas duplicadas
      const cedulas = firmantes.map(f => f.cedula).filter(c => c);
      const duplicadas = cedulas.filter((c, i) => cedulas.indexOf(c) !== i);
      if (duplicadas.length > 0) {
        newErrors.duplicados = `Hay cédulas duplicadas: ${[...new Set(duplicadas)].join(', ')}`;
      }

      if (erroresFirmantes.length > 0 || Object.keys(newErrors).length > 0) {
        newErrors.firmantes = erroresFirmantes;
        
        // Activar errores visuales (animaciones)
        setErroresVisuales(nuevosErroresVisuales);
        
        // Limpiar animaciones después de 1 segundo
        setTimeout(() => setErroresVisuales({}), 1000);
        
        // Scroll al primer error
        if (primerErrorIndex >= 0) {
          setTimeout(() => {
            const elemento = document.getElementById(`firmante-card-${firmantes[primerErrorIndex].id}`);
            if (elemento) {
              elemento.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
        }
        
        // Mostrar alerta flotante si hay errores de método
        const erroresMetodo = Object.keys(nuevosErroresVisuales).length;
        if (erroresMetodo > 0) {
          mostrarMensaje(
            `¡Atención! ${erroresMetodo} firmante${erroresMetodo > 1 ? 's' : ''} sin método de envío seleccionado`,
            'error'
          );
        }
      }
    }

    if (pasoActual === 3) {
      // Orden de firma siempre tiene un valor válido
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const avanzarPaso = () => {
    if (validarPaso(paso)) {
      setPaso(paso + 1);
      setMensaje('');
      setErrors({});
    }
  };

  const retrocederPaso = () => {
    setPaso(paso - 1);
    setMensaje('');
    setErrors({});
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // ENVIAR DOCUMENTO ADMINISTRATIVO
  // ═══════════════════════════════════════════════════════════════════════════
  const handleSubmit = async () => {
    setEnviando(true);
    setProcessingMessage('📤 Preparando envío...');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('documento', documento);
      formDataToSend.append('nombreDocumento', nombreDocumento);
      formDataToSend.append('descripcion', descripcionDocumento);
      formDataToSend.append('ordenFirma', ordenFirma);
      formDataToSend.append('tipo', 'administrativo');
      formDataToSend.append('firmantes', JSON.stringify(
        firmantes.map((f, i) => ({
          orden: i + 1,
          nombre: f.nombre,
          cedula: f.cedula,
          correo: f.correo,
          celular: f.celular,
          tipoFirmante: f.tipoFirmante,
          metodoEnvio: f.metodoEnvio
        }))
      ));

      setProcessingMessage('📤 Enviando documento...');

      const response = await apiClient.post('/api/send-admin-document', formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      });

      setProcessingMessage('✅ ¡Documento enviado!');
      mostrarMensaje(response.data.message || 'Documento enviado exitosamente', 'success');

      setTimeout(() => navigate('/administrative-documents'), 2000);

    } catch (error) {
      console.error('Error:', error);
      mostrarMensaje(error.response?.data?.message || 'Error al enviar el documento.', 'error');
    } finally {
      setEnviando(false);
      setProcessingMessage('');
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // NOMBRES DE PASOS PARA EL INDICADOR
  // ═══════════════════════════════════════════════════════════════════════════
  const pasos = [
    { num: 1, nombre: 'Documento' },
    { num: 2, nombre: 'Firmantes' },
    { num: 3, nombre: 'Orden' },
    { num: 4, nombre: 'Campos' },
    { num: 5, nombre: 'Revisar' }
  ];

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <>
      {/* ⭐ ESTILOS CSS PARA ANIMACIONES Y DRAG & DROP */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }
        
        .shake-error {
          animation: shake 0.5s ease-in-out;
          border-color: #dc3545 !important;
          box-shadow: 0 0 10px rgba(220, 53, 69, 0.5) !important;
        }
        
        .metodo-envio-error {
          background-color: #fff5f5 !important;
          border: 2px solid #dc3545 !important;
          border-radius: 8px;
          padding: 10px;
          animation: shake 0.5s ease-in-out;
        }
        
        .error-badge {
          animation: pulse 1s infinite;
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        .firmante-draggable {
          cursor: grab;
          transition: all 0.2s ease;
          user-select: none;
        }
        
        .firmante-draggable:active {
          cursor: grabbing;
        }
        
        .firmante-dragging {
          opacity: 0.5;
          transform: scale(1.02);
          box-shadow: 0 10px 30px rgba(0,0,0,0.2) !important;
        }
        
        .firmante-drag-over {
          border-top: 3px solid #198754 !important;
          margin-top: 10px;
          padding-top: 15px;
        }
        
        .drag-handle {
          cursor: grab;
          padding: 5px 10px;
          color: #6c757d;
          font-size: 1.2rem;
          transition: color 0.2s;
        }
        
        .drag-handle:hover {
          color: #198754;
        }
        
        .drag-handle:active {
          cursor: grabbing;
        }
      `}</style>

      <div className="d-flex">
        <Sidebar onToggle={setSidebarAbierto} />
        <div
          className="flex-grow-1"
          style={{
            marginLeft: sidebarAbierto ? '240px' : '80px',
            transition: 'margin-left 0.3s',
            padding: '20px 30px',
            minHeight: '100vh',
            backgroundColor: '#f8f9fa'
          }}
        >
          <div className="container-fluid">
            {/* ═══════════════════════════════════════════════════════════════ */}
            {/* HEADER */}
            {/* ═══════════════════════════════════════════════════════════════ */}
            <div className="mb-4">
              <div className="d-flex align-items-center justify-content-between">
                <h2 className="mb-1">📋 Enviar Documento Administrativo</h2>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => navigate('/send-document')}
                >
                  ← Volver
                </button>
              </div>
              <p className="text-muted mb-0">Contratos, actas y documentos con múltiples firmantes</p>
            </div>

            {/* ═══════════════════════════════════════════════════════════════ */}
            {/* INDICADOR DE 5 PASOS */}
            {/* ═══════════════════════════════════════════════════════════════ */}
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                {pasos.map((p, i) => (
                  <React.Fragment key={p.num}>
                    <div
                      className={`rounded-circle d-flex align-items-center justify-content-center fw-bold ${
                        paso >= p.num ? 'bg-success text-white' : 'bg-secondary text-white'
                      }`}
                      style={{ width: '40px', height: '40px', fontSize: '1rem', flexShrink: 0 }}
                    >
                      {paso > p.num ? '✓' : p.num}
                    </div>
                    {i < pasos.length - 1 && (
                      <div
                        className="flex-grow-1 mx-1"
                        style={{ height: '3px', backgroundColor: paso > p.num ? '#198754' : '#dee2e6' }}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
              <div className="d-flex justify-content-between px-1">
                {pasos.map(p => (
                  <small
                    key={p.num}
                    className={paso === p.num ? 'fw-bold text-success' : 'text-muted'}
                    style={{ fontSize: '0.75rem', textAlign: 'center', width: '60px' }}
                  >
                    {p.nombre}
                  </small>
                ))}
              </div>
            </div>

            {/* MENSAJES */}
            {mensaje && (
              <div className={`alert ${mensaje.includes('✅') ? 'alert-success' : mensaje.includes('❌') ? 'alert-danger' : 'alert-warning'} alert-dismissible`}>
                {mensaje}
                <button type="button" className="btn-close" onClick={() => setMensaje('')}></button>
              </div>
            )}

            {/* ═══════════════════════════════════════════════════════════════ */}
            {/* CONTENIDO DEL PASO ACTUAL */}
            {/* ═══════════════════════════════════════════════════════════════ */}
            <div className="card shadow-sm">
              <div className="card-body p-4">

                {/* ═══════════════════════════════════════════════════════ */}
                {/* PASO 1: SUBIR DOCUMENTO */}
                {/* ═══════════════════════════════════════════════════════ */}
                {paso === 1 && (
                  <div>
                    <h4 className="mb-4">📄 Documento a Firmar</h4>
                    
                    <div className="row">
                      <div className="col-12 mb-4">
                        <label className="form-label fw-bold">Archivo PDF *</label>
                        <div
                          className={`border rounded-3 p-5 text-center ${
                            documento ? 'border-success bg-success bg-opacity-10' : 'border-dashed'
                          }`}
                          style={{
                            borderStyle: documento ? 'solid' : 'dashed',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onClick={() => document.getElementById('fileInput').click()}
                        >
                          {documento ? (
                            <>
                              <div style={{ fontSize: '3rem' }}>✅</div>
                              <h5 className="mt-2 text-success">{documento.name}</h5>
                              <small className="text-muted">
                                {(documento.size / 1024 / 1024).toFixed(2)} MB · Click para cambiar
                              </small>
                            </>
                          ) : (
                            <>
                              <div style={{ fontSize: '3rem' }}>📎</div>
                              <h5 className="mt-2">Click aquí para subir el PDF</h5>
                              <small className="text-muted">Solo archivos PDF</small>
                            </>
                          )}
                        </div>
                        <input
                          id="fileInput"
                          type="file"
                          accept=".pdf"
                          onChange={handleFileChange}
                          className="d-none"
                        />
                        {errors.documento && (
                          <div className="text-danger small mt-1">{errors.documento}</div>
                        )}
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-bold">Nombre del documento *</label>
                        <input
                          type="text"
                          value={nombreDocumento}
                          onChange={(e) => {
                            setNombreDocumento(limpiarCaracteresEspeciales(e.target.value).toUpperCase());
                            setErrors(prev => ({ ...prev, nombreDocumento: '' }));
                          }}
                          className={`form-control form-control-lg ${errors.nombreDocumento ? 'is-invalid' : ''}`}
                          placeholder="Ej: CONTRATO DE PRESTACION DE SERVICIOS"
                        />
                        {errors.nombreDocumento && (
                          <div className="text-danger small mt-1">{errors.nombreDocumento}</div>
                        )}
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-bold">Descripción (opcional)</label>
                        <input
                          type="text"
                          value={descripcionDocumento}
                          onChange={(e) => setDescripcionDocumento(e.target.value)}
                          className="form-control form-control-lg"
                          placeholder="Ej: Contrato para proveedor de insumos"
                        />
                      </div>
                    </div>

                    <div className="d-flex justify-content-end mt-4">
                      <button type="button" className="btn btn-success btn-lg px-5" onClick={avanzarPaso}>
                        Siguiente →
                      </button>
                    </div>
                  </div>
                )}

                {/* ═══════════════════════════════════════════════════════ */}
                {/* PASO 2: AGREGAR FIRMANTES */}
                {/* ═══════════════════════════════════════════════════════ */}
                {paso === 2 && (
                  <div>
                    <div className="d-flex align-items-center justify-content-between mb-4">
                      <h4 className="mb-0">
                        👥 Firmantes 
                        <span className="badge bg-success ms-2">{firmantes.length}</span>
                      </h4>
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={agregarFirmante}
                      >
                        + Agregar Firmante
                      </button>
                    </div>

                    {errors.duplicados && (
                      <div className="alert alert-danger">{errors.duplicados}</div>
                    )}

                    {/* Lista de firmantes */}
                    {firmantes.map((firmante, index) => {
                      const color = obtenerColor(index);
                      const errFirmante = errors.firmantes?.[index] || {};

                      return (
                        <div
                          key={firmante.id}
                          id={`firmante-card-${firmante.id}`}
                          className={`card mb-3 ${erroresVisuales[`metodo-${firmante.id}`] ? 'shake-error' : ''}`}
                          style={{
                            borderLeft: `5px solid ${color.border}`,
                            backgroundColor: color.bg
                          }}
                        >
                          <div className="card-body">
                            {/* Encabezado del firmante */}
                            <div className="d-flex align-items-center justify-content-between mb-3">
                              <div className="d-flex align-items-center">
                                <span
                                  className="badge rounded-circle d-flex align-items-center justify-content-center me-2"
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    backgroundColor: color.border,
                                    color: 'white',
                                    fontSize: '0.9rem'
                                  }}
                                >
                                  {index + 1}
                                </span>
                                <strong style={{ color: color.text }}>
                                  Firmante {index + 1}
                                  {firmante.tipoFirmante === 'interno' && (
                                    <span className="badge bg-info ms-2" style={{ fontSize: '0.7rem' }}>👤 Interno</span>
                                  )}
                                  {firmante.tipoFirmante === 'externo' && firmante.nombre && (
                                    <span className="badge bg-warning text-dark ms-2" style={{ fontSize: '0.7rem' }}>🌐 Externo</span>
                                  )}
                                </strong>
                              </div>
                              {firmantes.length > 1 && (
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => eliminarFirmante(firmante.id)}
                                  title="Eliminar firmante"
                                >
                                  ✕
                                </button>
                              )}
                            </div>

                            {/* Campos del firmante */}
                            <div className="row">
                              <div className="col-md-3 mb-2">
                                <label className="form-label small fw-bold">Cédula *</label>
                                <div className="input-group">
                                  <input
                                    type="text"
                                    value={firmante.cedula}
                                    onChange={(e) => actualizarFirmante(firmante.id, 'cedula', limpiarCedula(e.target.value))}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        buscarPersona(firmante.id, firmante.cedula);
                                      }
                                    }}
                                    className={`form-control ${errFirmante.cedula ? 'is-invalid' : ''}`}
                                    placeholder="Cédula"
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={() => buscarPersona(firmante.id, firmante.cedula)}
                                    disabled={!firmante.cedula || firmante.buscando}
                                    title="Buscar por cédula"
                                  >
                                    {firmante.buscando ? (
                                      <span className="spinner-border spinner-border-sm"></span>
                                    ) : '🔍'}
                                  </button>
                                </div>
                                {errFirmante.cedula && <small className="text-danger">{errFirmante.cedula}</small>}
                              </div>

                              <div className="col-md-3 mb-2">
                                <label className="form-label small fw-bold">Nombre completo *</label>
                                <input
                                  type="text"
                                  value={firmante.nombre}
                                  onChange={(e) => actualizarFirmante(firmante.id, 'nombre', limpiarCaracteresEspeciales(e.target.value))}
                                  className={`form-control ${errFirmante.nombre ? 'is-invalid' : ''}`}
                                  placeholder="Nombres y apellidos"
                                />
                                {errFirmante.nombre && <small className="text-danger">{errFirmante.nombre}</small>}
                              </div>

                              <div className="col-md-3 mb-2">
                                <label className="form-label small fw-bold">Correo</label>
                                <input
                                  type="email"
                                  value={firmante.correo}
                                  onChange={(e) => actualizarFirmante(firmante.id, 'correo', e.target.value)}
                                  className={`form-control ${errFirmante.correo ? 'is-invalid' : ''}`}
                                  placeholder="correo@ejemplo.com"
                                />
                                {errFirmante.correo && <small className="text-danger">{errFirmante.correo}</small>}
                              </div>

                              <div className="col-md-3 mb-2">
                                <label className="form-label small fw-bold">Celular</label>
                                <input
                                  type="tel"
                                  value={firmante.celular}
                                  onChange={(e) => actualizarFirmante(firmante.id, 'celular', limpiarCedula(e.target.value))}
                                  className={`form-control ${errFirmante.celular ? 'is-invalid' : ''}`}
                                  placeholder="3001234567"
                                  maxLength="10"
                                />
                                {errFirmante.celular && <small className="text-danger">{errFirmante.celular}</small>}
                              </div>
                            </div>

                            {/* Contacto mínimo requerido */}
                            {errFirmante.contacto && (
                              <small className="text-danger d-block mt-1">{errFirmante.contacto}</small>
                            )}

                            {/* ⭐ Método de envío MEJORADO */}
                            <div 
                              className={`mt-3 p-2 rounded ${
                                erroresVisuales[`metodo-${firmante.id}`] ? 'metodo-envio-error' : 'bg-white bg-opacity-50'
                              }`}
                              style={{ 
                                border: errFirmante.metodoEnvio ? '2px solid #dc3545' : '1px solid #dee2e6',
                                transition: 'all 0.3s ease'
                              }}
                            >
                              <div className="d-flex align-items-center gap-3 flex-wrap">
                                <small className="fw-bold" style={{ color: errFirmante.metodoEnvio ? '#dc3545' : '#6c757d' }}>
                                  📤 Enviar por:
                                </small>
                                
                                <div className="form-check form-check-inline">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={firmante.metodoEnvio.includes('correo')}
                                    onChange={() => toggleMetodoEnvio(firmante.id, 'correo')}
                                    disabled={!firmante.correo}
                                    id={`correo-${firmante.id}`}
                                    style={{ transform: 'scale(1.2)' }}
                                  />
                                  <label 
                                    className={`form-check-label ${!firmante.correo ? 'text-muted' : ''}`} 
                                    htmlFor={`correo-${firmante.id}`}
                                  >
                                    📧 Correo
                                  </label>
                                </div>
                                
                                <div className="form-check form-check-inline">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={firmante.metodoEnvio.includes('sms')}
                                    onChange={() => toggleMetodoEnvio(firmante.id, 'sms')}
                                    disabled={!firmante.celular}
                                    id={`sms-${firmante.id}`}
                                    style={{ transform: 'scale(1.2)' }}
                                  />
                                  <label 
                                    className={`form-check-label ${!firmante.celular ? 'text-muted' : ''}`} 
                                    htmlFor={`sms-${firmante.id}`}
                                  >
                                    📱 SMS
                                  </label>
                                </div>
                                
                                {/* Badge de error animado */}
                                {errFirmante.metodoEnvio && (
                                  <span className="badge bg-danger error-badge d-flex align-items-center gap-1">
                                    <span style={{ fontSize: '1rem' }}>⚠️</span>
                                    {errFirmante.metodoEnvio}
                                  </span>
                                )}
                              </div>
                              
                              {/* Ayuda contextual */}
                              {(!firmante.correo && !firmante.celular) && (
                                <small className="text-warning d-block mt-1">
                                  💡 Ingresa correo o celular para habilitar los métodos de envío
                                </small>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {/* Botón para agregar más */}
                    {firmantes.length < 50 && (
                      <div className="text-center mb-3">
                        <button
                          type="button"
                          className="btn btn-outline-success"
                          onClick={agregarFirmante}
                        >
                          + Agregar otro firmante ({firmantes.length}/50)
                        </button>
                      </div>
                    )}

                    <div className="d-flex justify-content-between mt-4">
                      <button type="button" className="btn btn-outline-secondary btn-lg" onClick={retrocederPaso}>
                        ← Atrás
                      </button>
                      <button type="button" className="btn btn-success btn-lg px-5" onClick={avanzarPaso}>
                        Siguiente →
                      </button>
                    </div>
                  </div>
                )}

                {/* ═══════════════════════════════════════════════════════ */}
                {/* PASO 3: ORDEN DE FIRMA */}
                {/* ═══════════════════════════════════════════════════════ */}
                {paso === 3 && (
                  <div>
                    <h4 className="mb-4">🔄 Orden de Firma</h4>
                    <p className="text-muted mb-4">
                      ¿Cómo deben firmar los {firmantes.length} firmantes?
                    </p>

                    <div className="row justify-content-center mb-4">
                      {/* Opción: Paralelo */}
                      <div className="col-md-5 mb-3">
                        <div
                          className={`card h-100 ${ordenFirma === 'paralelo' ? 'border-success border-3' : 'border-secondary'}`}
                          style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                          onClick={() => setOrdenFirma('paralelo')}
                        >
                          <div className="card-body text-center p-4">
                            <div style={{ fontSize: '3rem' }}>⚡</div>
                            <h5 className="mt-2">Todos al Tiempo</h5>
                            <p className="text-muted small mb-2">
                              Todos los firmantes reciben el documento simultáneamente 
                              y pueden firmar en cualquier orden.
                            </p>
                            <div className="d-flex justify-content-center align-items-center gap-1 mt-3">
                              {firmantes.slice(0, 5).map((f, i) => (
                                <span
                                  key={f.id}
                                  className="badge rounded-circle d-flex align-items-center justify-content-center"
                                  style={{
                                    width: '30px',
                                    height: '30px',
                                    backgroundColor: obtenerColor(i).border,
                                    color: 'white'
                                  }}
                                >
                                  {i + 1}
                                </span>
                              ))}
                              {firmantes.length > 5 && (
                                <small className="text-muted">+{firmantes.length - 5}</small>
                              )}
                            </div>
                            <small className="text-muted d-block mt-2">
                              Más rápido · Ideal para comités
                            </small>
                            {ordenFirma === 'paralelo' && (
                              <div className="mt-2">
                                <span className="badge bg-success">✓ Seleccionado</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Opción: Secuencial */}
                      <div className="col-md-5 mb-3">
                        <div
                          className={`card h-100 ${ordenFirma === 'secuencial' ? 'border-success border-3' : 'border-secondary'}`}
                          style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                          onClick={() => setOrdenFirma('secuencial')}
                        >
                          <div className="card-body text-center p-4">
                            <div style={{ fontSize: '3rem' }}>📝</div>
                            <h5 className="mt-2">En Orden</h5>
                            <p className="text-muted small mb-2">
                              Firman uno por uno. El siguiente recibe el documento 
                              solo cuando el anterior haya firmado.
                            </p>
                            <div className="d-flex justify-content-center align-items-center gap-1 mt-3">
                              {firmantes.slice(0, 5).map((f, i) => (
                                <React.Fragment key={f.id}>
                                  <span
                                    className="badge rounded-circle d-flex align-items-center justify-content-center"
                                    style={{
                                      width: '30px',
                                      height: '30px',
                                      backgroundColor: obtenerColor(i).border,
                                      color: 'white'
                                    }}
                                  >
                                    {i + 1}
                                  </span>
                                  {i < Math.min(firmantes.length, 5) - 1 && (
                                    <span style={{ fontSize: '0.8rem' }}>→</span>
                                  )}
                                </React.Fragment>
                              ))}
                              {firmantes.length > 5 && (
                                <small className="text-muted">+{firmantes.length - 5}</small>
                              )}
                            </div>
                            <small className="text-muted d-block mt-2">
                              Más control · Ideal para contratos
                            </small>
                            {ordenFirma === 'secuencial' && (
                              <div className="mt-2">
                                <span className="badge bg-success">✓ Seleccionado</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ⭐ Reordenar firmantes con DRAG & DROP */}
                    {ordenFirma === 'secuencial' && (
                      <div className="mt-4">
                        <h5 className="mb-3">📋 Orden de los firmantes</h5>
                        <p className="text-muted small mb-3">
                          <span className="badge bg-info me-2">💡 Tip</span>
                          <strong>Arrastra y suelta</strong> los firmantes para cambiar el orden. 
                          El firmante 1 será el primero en recibir el documento.
                        </p>
                        
                        <div className="list-group">
                          {firmantes.map((f, index) => {
                            const color = obtenerColor(index);
                            const isDragging = firmanteArrastrado === index;
                            const isDragOver = indiceDestino === index && firmanteArrastrado !== index;
                            
                            return (
                              <div
                                key={f.id}
                                draggable
                                onDragStart={(e) => handleDragStart(e, index)}
                                onDragEnd={handleDragEnd}
                                onDragOver={(e) => handleDragOver(e, index)}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleDrop(e, index)}
                                className={`list-group-item d-flex align-items-center firmante-draggable ${
                                  isDragging ? 'firmante-dragging' : ''
                                } ${isDragOver ? 'firmante-drag-over' : ''}`}
                                style={{ 
                                  borderLeft: `4px solid ${color.border}`,
                                  backgroundColor: isDragOver ? '#e8f5e9' : 'white',
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                {/* Handle para arrastrar */}
                                <div className="drag-handle me-2" title="Arrastra para reordenar">
                                  ⠿
                                </div>

                                {/* Número de orden */}
                                <span
                                  className="badge rounded-circle d-flex align-items-center justify-content-center me-3"
                                  style={{
                                    width: '35px',
                                    height: '35px',
                                    backgroundColor: color.border,
                                    color: 'white',
                                    fontSize: '1rem',
                                    flexShrink: 0
                                  }}
                                >
                                  {index + 1}
                                </span>

                                {/* Datos del firmante */}
                                <div className="flex-grow-1">
                                  <strong>{f.nombre || '(sin nombre)'}</strong>
                                  <br />
                                  <small className="text-muted">
                                    CC {f.cedula || '---'} · {f.correo || f.celular || 'sin contacto'}
                                  </small>
                                </div>

                                {/* Indicador de cuándo firma */}
                                <div className="d-flex flex-column align-items-end">
                                  <span className={`badge ${index === 0 ? 'bg-success' : 'bg-secondary'}`}>
                                    {index === 0 ? '🥇 Firma primero' : `Firma después de #${index}`}
                                  </span>
                                  {index > 0 && (
                                    <small className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                                      Espera que #{index} firme
                                    </small>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        
                        {/* Indicador visual de instrucciones */}
                        <div className="text-center mt-3 p-2 bg-light rounded">
                          <small className="text-muted">
                            🖱️ <strong>Arrastra</strong> los firmantes hacia arriba o abajo para cambiar su orden
                          </small>
                        </div>
                      </div>
                    )}

                    <div className="d-flex justify-content-between mt-4">
                      <button type="button" className="btn btn-outline-secondary btn-lg" onClick={retrocederPaso}>
                        ← Atrás
                      </button>
                      <button type="button" className="btn btn-success btn-lg px-5" onClick={avanzarPaso}>
                        Siguiente →
                      </button>
                    </div>
                  </div>
                )}

                {/* ═══════════════════════════════════════════════════════ */}
                {/* PASO 4: UBICAR CAMPOS (PLACEHOLDER) */}
                {/* ═══════════════════════════════════════════════════════ */}
                {paso === 4 && (
                  <div>
                    <h4 className="mb-4">📌 Ubicar Campos en el Documento</h4>
                    
                    <div className="text-center py-5">
                      <div style={{ fontSize: '5rem', opacity: 0.5 }}>🚧</div>
                      <h5 className="mt-3 text-muted">Editor Visual — Próximamente</h5>
                      <p className="text-muted" style={{ maxWidth: '500px', margin: '0 auto' }}>
                        Aquí podrás arrastrar y colocar campos de firma, texto, fecha e iniciales 
                        directamente sobre el PDF. Cada campo se asignará a un firmante específico 
                        identificado por color.
                      </p>
                      
                      <div className="alert alert-info mt-4 mx-auto" style={{ maxWidth: '600px' }}>
                        <strong>🔧 Por ahora:</strong> Se asignará automáticamente un campo de firma 
                        para cada firmante en posiciones predeterminadas. En la siguiente actualización 
                        podrás personalizar la ubicación de cada campo.
                      </div>

                      {/* Preview de firmantes y sus colores */}
                      <div className="mt-4">
                        <h6>Firmantes configurados:</h6>
                        <div className="d-flex justify-content-center flex-wrap gap-2 mt-2">
                          {firmantes.map((f, i) => {
                            const color = obtenerColor(i);
                            return (
                              <span
                                key={f.id}
                                className="badge px-3 py-2"
                                style={{
                                  backgroundColor: color.bg,
                                  color: color.text,
                                  border: `2px solid ${color.border}`,
                                  fontSize: '0.85rem'
                                }}
                              >
                                ✍️ {f.nombre || `Firmante ${i + 1}`}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    <div className="d-flex justify-content-between mt-4">
                      <button type="button" className="btn btn-outline-secondary btn-lg" onClick={retrocederPaso}>
                        ← Atrás
                      </button>
                      <button type="button" className="btn btn-success btn-lg px-5" onClick={avanzarPaso}>
                        Siguiente →
                      </button>
                    </div>
                  </div>
                )}

                {/* ═══════════════════════════════════════════════════════ */}
                {/* PASO 5: REVISAR Y ENVIAR */}
                {/* ═══════════════════════════════════════════════════════ */}
                {paso === 5 && (
                  <div>
                    <h4 className="mb-4">✅ Revisar y Enviar</h4>

                    {/* Resumen del documento */}
                    <div className="card mb-3">
                      <div className="card-header bg-light d-flex justify-content-between align-items-center">
                        <strong>📄 Documento</strong>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => setPaso(1)}
                        >
                          Editar
                        </button>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-6">
                            <small className="text-muted">Nombre:</small><br />
                            <strong>{nombreDocumento}</strong>
                          </div>
                          <div className="col-md-6">
                            <small className="text-muted">Archivo:</small><br />
                            <strong>{documento?.name}</strong>
                            <small className="text-muted ms-2">
                              ({(documento?.size / 1024 / 1024).toFixed(2)} MB)
                            </small>
                          </div>
                          {descripcionDocumento && (
                            <div className="col-12 mt-2">
                              <small className="text-muted">Descripción:</small><br />
                              {descripcionDocumento}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Resumen de firmantes */}
                    <div className="card mb-3">
                      <div className="card-header bg-light d-flex justify-content-between align-items-center">
                        <strong>👥 Firmantes ({firmantes.length})</strong>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => setPaso(2)}
                        >
                          Editar
                        </button>
                      </div>
                      <div className="card-body p-0">
                        <div className="table-responsive">
                          <table className="table table-sm mb-0">
                            <thead className="table-light">
                              <tr>
                                <th style={{ width: '40px' }}>#</th>
                                <th>Nombre</th>
                                <th>Cédula</th>
                                <th>Contacto</th>
                                <th>Envío</th>
                                <th>Tipo</th>
                              </tr>
                            </thead>
                            <tbody>
                              {firmantes.map((f, i) => {
                                const color = obtenerColor(i);
                                return (
                                  <tr key={f.id}>
                                    <td>
                                      <span
                                        className="badge rounded-circle d-inline-flex align-items-center justify-content-center"
                                        style={{
                                          width: '25px',
                                          height: '25px',
                                          backgroundColor: color.border,
                                          color: 'white',
                                          fontSize: '0.75rem'
                                        }}
                                      >
                                        {i + 1}
                                      </span>
                                    </td>
                                    <td><strong>{f.nombre}</strong></td>
                                    <td>{f.cedula}</td>
                                    <td>
                                      {f.correo && <small className="d-block">📧 {f.correo}</small>}
                                      {f.celular && <small className="d-block">📱 {f.celular}</small>}
                                    </td>
                                    <td>
                                      {f.metodoEnvio.map(m => (
                                        <span key={m} className="badge bg-secondary me-1">
                                          {m === 'correo' ? '📧' : '📱'} {m}
                                        </span>
                                      ))}
                                    </td>
                                    <td>
                                      <span className={`badge ${f.tipoFirmante === 'interno' ? 'bg-info' : 'bg-warning text-dark'}`}>
                                        {f.tipoFirmante === 'interno' ? '👤 Interno' : '🌐 Externo'}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    {/* Resumen de orden */}
                    <div className="card mb-3">
                      <div className="card-header bg-light d-flex justify-content-between align-items-center">
                        <strong>🔄 Orden de Firma</strong>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => setPaso(3)}
                        >
                          Editar
                        </button>
                      </div>
                      <div className="card-body">
                        <div className="d-flex align-items-center">
                          <span style={{ fontSize: '1.5rem' }} className="me-2">
                            {ordenFirma === 'paralelo' ? '⚡' : '📝'}
                          </span>
                          <div>
                            <strong>
                              {ordenFirma === 'paralelo' ? 'Todos al Tiempo (Paralelo)' : 'En Orden (Secuencial)'}
                            </strong>
                            <br />
                            <small className="text-muted">
                              {ordenFirma === 'paralelo'
                                ? 'Todos los firmantes recibirán el documento simultáneamente'
                                : `Firmarán en orden: ${firmantes.map((f, i) => `${i + 1}. ${f.nombre || '?'}`).join(' → ')}`
                              }
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Aviso Certicámara */}
                    <div className="alert alert-success">
                      <strong>🔐 Sello Digital:</strong> Una vez que todos los firmantes completen su firma, 
                      el documento será enviado automáticamente a Certicámara para el sello digital de certificación.
                    </div>

                    {/* Botones */}
                    <div className="d-flex justify-content-between mt-4">
                      <button type="button" className="btn btn-outline-secondary btn-lg" onClick={retrocederPaso}>
                        ← Atrás
                      </button>
                      <button
                        type="button"
                        className="btn btn-success btn-lg px-5"
                        onClick={handleSubmit}
                        disabled={enviando}
                      >
                        {enviando ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Enviando...
                          </>
                        ) : (
                          `📤 Enviar a ${firmantes.length} firmante${firmantes.length > 1 ? 's' : ''}`
                        )}
                      </button>
                    </div>
                  </div>
                )}

              </div>
            </div>

            {/* LOADING OVERLAY */}
            {enviando && (
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0,0,0,0.7)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 9999
                }}
              >
                <div className="text-center bg-white p-5 rounded shadow-lg">
                  <div className="spinner-border text-success mb-3" style={{ width: '4rem', height: '4rem' }}></div>
                  <h4>{processingMessage}</h4>
                  <p className="text-muted mb-0">Por favor espera...</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default AdminSendDocumentPage;